import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePreviewTemplate } from '../../api/templates.api';

interface TemplateEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

export function TemplateEditor({ value, onChange, readOnly }: TemplateEditorProps) {
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewVars, setPreviewVars] = useState('{}');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { preview } = usePreviewTemplate();

  const updatePreview = useCallback(
    async (mjml: string, vars: string) => {
      if (!mjml.trim()) {
        setPreviewHtml('');
        setPreviewError(null);
        return;
      }
      let parsedVars: Record<string, unknown> = {};
      try {
        parsedVars = JSON.parse(vars) as Record<string, unknown>;
      } catch {
        // Invalid JSON — render without variables
      }
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const result = await preview(mjml, parsedVars);
        setPreviewHtml(result.bodyHtml ?? '');
      } catch (err) {
        setPreviewError(err instanceof Error ? err.message : 'Preview failed');
        setPreviewHtml('');
      } finally {
        setPreviewLoading(false);
      }
    },
    [preview],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void updatePreview(value, previewVars);
    }, 800);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, previewVars, updatePreview]);

  const handleCodeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <div
        style={{
          display: 'flex',
          minHeight: 400,
          border: '1px solid #E2E8F0',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        {/* Code Editor */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRight: '4px solid #E2E8F0',
          }}
        >
          <div
            style={{
              padding: '8px 12px',
              backgroundColor: '#F8FAFC',
              borderBottom: '1px solid #E2E8F0',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: '#64748B',
            }}
          >
            MJML + Handlebars
          </div>
          <textarea
            value={value}
            onChange={handleCodeChange}
            readOnly={readOnly}
            spellCheck={false}
            style={{
              flex: 1,
              padding: 16,
              fontFamily: "'Fira Code', 'JetBrains Mono', monospace",
              fontSize: '0.875rem',
              lineHeight: 1.6,
              border: 'none',
              resize: 'none',
              outline: 'none',
              backgroundColor: '#FFFFFF',
              tabSize: 2,
            }}
          />
        </div>

        {/* Preview */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              padding: '8px 12px',
              backgroundColor: '#F8FAFC',
              borderBottom: '1px solid #E2E8F0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B' }}>Preview</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {(['desktop', 'mobile'] as const).map((mode) => (
                <button
                  type="button"
                  key={mode}
                  onClick={() => setPreviewMode(mode)}
                  style={{
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                    fontWeight: previewMode === mode ? 600 : 400,
                    color: previewMode === mode ? '#2563EB' : '#64748B',
                    backgroundColor: previewMode === mode ? '#DBEAFE' : 'transparent',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                  }}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>
          <div
            style={{
              flex: 1,
              padding: 16,
              backgroundColor: '#F8FAFC',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            {previewLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  fontSize: '0.75rem',
                  color: '#64748B',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    border: '2px solid #94A3B8',
                    borderTopColor: '#2563EB',
                    borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite',
                  }}
                />
                Rendering...
              </div>
            )}
            {previewError ? (
              <div
                style={{
                  width: '100%',
                  padding: 16,
                  backgroundColor: '#FEF2F2',
                  borderRadius: 6,
                  border: '1px solid #FECACA',
                  color: '#DC2626',
                  fontSize: '0.8rem',
                  fontFamily: "'Fira Code', monospace",
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {previewError}
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                title="Template Preview"
                // Fully sandboxed: no scripts, and — critically — no
                // allow-same-origin. Without it, srcDoc content shares this
                // page's origin and can reach window.parent, localStorage and
                // the session token from it. The preview is arbitrary
                // author-supplied MJML/HTML, and email clients do not run
                // scripts anyway, so there is nothing to give up here.
                sandbox=""
                srcDoc={
                  previewHtml ||
                  '<p style="font-family:sans-serif;color:#64748B;text-align:center;padding:40px">Start typing MJML to see a preview</p>'
                }
                style={{
                  width: previewMode === 'mobile' ? 375 : '100%',
                  height: '100%',
                  minHeight: 350,
                  border: previewMode === 'mobile' ? '1px solid #E2E8F0' : 'none',
                  borderRadius: previewMode === 'mobile' ? 8 : 0,
                  backgroundColor: '#FFFFFF',
                }}
              />
            )}
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>

      {/* Preview Variables */}
      <details style={{ marginTop: 8 }}>
        <summary
          style={{
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#64748B',
            padding: '8px 0',
          }}
        >
          Preview Variables
        </summary>
        <textarea
          value={previewVars}
          onChange={(e) => setPreviewVars(e.target.value)}
          style={{
            width: '100%',
            minHeight: 80,
            padding: 12,
            fontFamily: "'Fira Code', 'JetBrains Mono', monospace",
            fontSize: '0.75rem',
            border: '1px solid #E2E8F0',
            borderRadius: 6,
            resize: 'vertical',
            outline: 'none',
          }}
        />
      </details>
    </div>
  );
}
